import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Stack,
  TextField,
} from "@mui/material";
import { FormDialog } from "../form";
import { DataTable } from "../data";
import { EmptyState } from "../feedback";

const LOCATION_CATEGORIES = [
  { key: "food", label: "Food (Food)" },
  { key: "food_and_drink", label: "Food and Drink (Food)" },
  { key: "restaurant", label: "Restaurant (Food)" },
  { key: "fast_food", label: "Fast Food (Food)" },
  { key: "cafe", label: "Cafe (Food)" },
  { key: "coffee", label: "Coffee (Food)" },
  { key: "coffee_shop", label: "Coffee Shop (Food)" },
  { key: "bakery", label: "Bakery (Food)" },
  { key: "pizza_restaurant", label: "Pizza Restaurant (Food)" },
  { key: "burger_restaurant", label: "Burger Restaurant (Food)" },
  { key: "deli", label: "Deli (Food)" },
  { key: "sandwich_shop", label: "Sandwich Shop (Food)" },
  { key: "snack_bar", label: "Snack Bar (Food)" },
  { key: "juice_bar", label: "Juice Bar (Food)" },
  { key: "ice_cream", label: "Ice Cream (Food)" },
  { key: "dessert_shop", label: "Dessert Shop (Food)" },
  { key: "seafood_restaurant", label: "Seafood Restaurant (Food)" },
  { key: "barbeque_restaurant", label: "Barbeque Restaurant (Food)" },
  { key: "breakfast_restaurant", label: "Breakfast Restaurant (Food)" },
  { key: "brunch_restaurant", label: "Brunch Restaurant (Food)" },
  { key: "food_court", label: "Food Court (Food)" },
  { key: "health_food_store", label: "Health Food Store (Food)" },
  { key: "liquor_store", label: "Liquor Store (Food)" },
  { key: "winery", label: "Winery (Food)" },
  { key: "bar", label: "Bar (Food)" },
  { key: "pub", label: "Pub (Food)" },
  { key: "teahouse", label: "Tea House (Food)" },
  { key: "convenience_store", label: "Convenience Store (Food/Non-food)" },
  { key: "farm", label: "Farm (Non-food)" },
  { key: "internet_cafe", label: "Internet Cafe (Non-food)" },
  { key: "supermarket", label: "Supermarket (Non-food)" },
  { key: "grocery", label: "Grocery (Non-food)" },
  { key: "butcher_shop", label: "Butcher Shop (Non-food)" },
  { key: "shopping", label: "Shopping (Non-food)" },
  { key: "clothing_store", label: "Clothing Store (Non-food)" },
  { key: "clothing", label: "Clothing (Non-food)" },
  { key: "womens_clothing_store", label: "Women's Clothing Store (Non-food)" },
  { key: "shoe_store", label: "Shoe Store (Non-food)" },
  { key: "electronics_shop", label: "Electronics Shop (Non-food)" },
  { key: "hardware_store", label: "Hardware Store (Non-food)" },
  { key: "furniture_store", label: "Furniture Store (Non-food)" },
  { key: "furniture_maker", label: "Furniture Maker (Non-food)" },
  { key: "jewelry_store", label: "Jewelry Store (Non-food)" },
  { key: "gift_shop", label: "Gift Shop (Non-food)" },
  { key: "book_store", label: "Book Store (Non-food)" },
  { key: "toy_store", label: "Toy Store (Non-food)" },
  { key: "pet_store", label: "Pet Store (Non-food)" },
  { key: "sports_shop", label: "Sports Shop (Non-food)" },
  { key: "video_game_store", label: "Video Game Store (Non-food)" },
  { key: "antique_shop", label: "Antique Shop (Non-food)" },
  { key: "fabric_store", label: "Fabric Store (Non-food)" },
  { key: "lighting_store", label: "Lighting Store (Non-food)" },
  { key: "garden_store", label: "Garden Store (Non-food)" },
  { key: "photo_store", label: "Photo Store (Non-food)" },
  { key: "music_shop", label: "Music Shop (Non-food)" },
  { key: "baby_goods_shop", label: "Baby Goods Shop (Non-food)" },
  { key: "fashion_accessory_shop", label: "Fashion Accessory Shop (Non-food)" },
  { key: "department_store", label: "Department Store (Non-food)" },
  { key: "wholesale_store", label: "Wholesale Store (Non-food)" },
  { key: "laundry", label: "Laundy (Non-food)" },
  { key: "services", label: "Services (Non-food)" },
];

const HOME_CENTER = { latitude: "9.1786", longitude: "124.7171" };

export default function LocationSearchDialog({
  open,
  onClose,
  locationCenter,
  setLocationCenter,
  query,
  setQuery,
  category,
  setCategory,
  radius,
  setRadius,
  results,
  loading,
  notice,
  onSearch,
  onUseCurrentLocation,
  onPick,
}) {
  const columns = [
    {
      id: "name",
      label: "Name",
      primary: true,
      render: (location) => location.placeName,
    },
    {
      id: "address",
      label: "Address",
      render: (location) => location.address || "—",
    },
    {
      id: "category",
      label: "Category",
      render: (location) => location.category || "—",
    },
    {
      id: "distance",
      label: "Distance (km)",
      align: "right",
      render: (location) =>
        location.distanceInKm ? location.distanceInKm.toFixed(2) : "—",
    },
    {
      id: "actions",
      label: "",
      align: "right",
      hideLabelOnMobile: true,
      render: (location) => (
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" onClick={() => onPick(location)}>
            Use
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
              const { latitude: cLat, longitude: cLng } = locationCenter;
              const mapUrl = `https://maps.googleapis.com/maps/api/staticmap?size=600x400&markers=color:blue|label:Me|${cLat},${cLng}&markers=color:red|label:Ve|${location.latitude},${location.longitude}&key=${apiKey}`;
              window.open(mapUrl, "_blank");
            }}
          >
            Map
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title="Browse locations"
      subtitle="Find a vendor location from the locations database."
      submitLabel={null}
      onSubmit={null}
      cancelLabel="Close"
      maxWidth="md"
    >
      <Stack spacing={2}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", md: "1fr 1fr 1fr" },
            gap: 1.5,
          }}
        >
          <TextField
            size="small"
            label="Center latitude"
            value={locationCenter.latitude}
            onChange={(event) =>
              setLocationCenter((prev) => ({
                ...prev,
                latitude: event.target.value,
              }))
            }
          />
          <TextField
            size="small"
            label="Center longitude"
            value={locationCenter.longitude}
            onChange={(event) =>
              setLocationCenter((prev) => ({
                ...prev,
                longitude: event.target.value,
              }))
            }
          />
          <TextField
            size="small"
            label="Radius (meters)"
            value={radius}
            onChange={(event) => setRadius(event.target.value)}
          />
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
            gap: 1.5,
          }}
        >
          <TextField
            size="small"
            label="Search query"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <Autocomplete
            size="small"
            options={LOCATION_CATEGORIES}
            getOptionLabel={(option) => option.label}
            value={
              LOCATION_CATEGORIES.find((cat) => cat.key === category) || null
            }
            onChange={(_, newValue) => setCategory(newValue ? newValue.key : "")}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Category" />
            )}
          />
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            variant="outlined"
            onClick={onUseCurrentLocation}
            disabled={loading}
          >
            Use current location
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setLocationCenter(HOME_CENTER)}
          >
            Home center point
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={onSearch}
            disabled={loading}
          >
            Search
          </Button>
        </Stack>

        {notice && <Alert severity="warning">{notice}</Alert>}

        <Box>
          {results && results.length > 0 ? (
            <DataTable columns={columns} rows={results} rowKey="id" />
          ) : (
            <EmptyState
              title="No locations yet"
              description="Set a center and search radius, then press Search."
              dense
            />
          )}
        </Box>
      </Stack>
    </FormDialog>
  );
}
